import { Guid } from "@/Common/Models/Guid";
import { NonFunctionProperties } from "@/Common/Models/NonFunctionProperties";
import { FlowsheetConstants } from "../FlowsheetConstants";
import { CareComponent } from "./CareElements/CareComponent";
import { CareElement } from "./CareElements/CareElement";
import { CareElementType } from "./CareElements/CareElementType";
import { FlowsheetEventHistory } from "./FlowsheetEventHistory";
import { FlowsheetFilterSettings } from "./FlowsheetFilterSettings";

/**
 * Converts an array of objects with a Name property into a record.
 * The keys of the record are the Name properties of the objects, and the values are the objects themselves.
 * @param hasName - The array of objects that have a Name property
 * @returns A record where the keys are the Name properties of the objects and the values are the objects themselves
 */
function toRecord<T extends { Name: string }>(hasName: T[]): Record<string, T> {
    return hasName.reduce((accumulator, hasName) => {
        accumulator[hasName.Name] = hasName;
        return accumulator;
    }, {} as Record<string, T>);
}
export class Flowsheet {
    public static readonly ImmutableCareComponents: string[] = [
        FlowsheetConstants.LabResults.CareComponentName,
        FlowsheetConstants.VitalSigns.CareComponentName
    ];

    public Id: Guid;
    public EncounterId: Guid;
    public TemplateId: Guid;
    public CreatedByEmployeeId: Guid;
    public CreationDate: Date;
    public ApprovedByEmployeeId?: Guid;
    public ApprovalDate?: Date;
    public EventHistory?: FlowsheetEventHistory[];
    public FilterSettings?: FlowsheetFilterSettings;

    public CareComponents?: CareComponent[];

    constructor(args: NonFunctionProperties<Flowsheet>) {
        Object.assign(this, args);
    }

    /**
     * Checks if a care component is immutable.
     * A care component is considered immutable if it is backend-loaded.
     * @param careComponent The care component to check
     */
    private isCareComponentImmutable(careComponent: CareComponent): boolean {
        return Flowsheet.ImmutableCareComponents.contains(careComponent.Name);
    }

    /**
     * Returns a new array of care elements, where each element is replaced by the old one if it exists.
     * @param currentCareElements The current care elements
     * @param oldCareElements The old care elements to override with
     */
    private overrideCareElements(
        currentCareElements: CareElement[],
        oldCareElements: CareElement[]
    ): CareElement[] {
        const oldCareElementsMap = toRecord(oldCareElements);
        return currentCareElements.map(currentCareElement => {
            if (oldCareElementsMap[currentCareElement.Name])
                this.updateCareElement(oldCareElementsMap[currentCareElement.Name], currentCareElement);

            return oldCareElementsMap[currentCareElement.Name] ?? currentCareElement;
        });
    }

    public updateCareElement(oldCareElement: CareElement, newCareElement: CareElement): void {
        if (oldCareElement.Name !== newCareElement.Name && oldCareElement.CareElementType !== newCareElement.CareElementType)
            return;

        switch (newCareElement.CareElementType) {
            case CareElementType.RiskFactor:
                if (oldCareElement.CareElementType === CareElementType.RiskFactor && newCareElement.CareElementType === CareElementType.RiskFactor)
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                break;
            case CareElementType.Immunization:
                if (oldCareElement.CareElementType === CareElementType.Immunization && newCareElement.CareElementType === CareElementType.Immunization)
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                break;
            case CareElementType.TextReviewed:
                if (oldCareElement.CareElementType === CareElementType.TextReviewed && newCareElement.CareElementType === CareElementType.TextReviewed) {
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.BooleanReviewed:
                if (oldCareElement.CareElementType === CareElementType.BooleanReviewed && newCareElement.CareElementType === CareElementType.BooleanReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.BooleanTextReviewed:
                if (oldCareElement.CareElementType === CareElementType.BooleanTextReviewed && newCareElement.CareElementType === CareElementType.BooleanTextReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.IntegerReviewed:
                if (oldCareElement.CareElementType === CareElementType.IntegerReviewed && newCareElement.CareElementType === CareElementType.IntegerReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.DecimalReviewed:
                if (oldCareElement.CareElementType === CareElementType.DecimalReviewed && newCareElement.CareElementType === CareElementType.DecimalReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.DateOnlyReviewed:
                if (oldCareElement.CareElementType === CareElementType.DateOnlyReviewed && newCareElement.CareElementType === CareElementType.DateOnlyReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.MultiSelectReviewed:
                if (oldCareElement.CareElementType === CareElementType.MultiSelectReviewed && newCareElement.CareElementType === CareElementType.MultiSelectReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                    oldCareElement.Operator = newCareElement.Operator ?? oldCareElement.Operator;
                    oldCareElement.Target = newCareElement.Target ?? oldCareElement.Target;
                }
                break;
            case CareElementType.Subject:
                if (oldCareElement.CareElementType === CareElementType.Subject && newCareElement
                    .CareElementType === CareElementType.Subject) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                }
                break;
            case CareElementType.FirstLineReviewed:
                if (oldCareElement.CareElementType === CareElementType.FirstLineReviewed && newCareElement.CareElementType === CareElementType.FirstLineReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                }
                break;
            case CareElementType.AgentDoseReviewed:
                if (oldCareElement.CareElementType === CareElementType.AgentDoseReviewed && newCareElement.CareElementType === CareElementType.AgentDoseReviewed) {
                    oldCareElement.Frequency = newCareElement.Frequency ?? oldCareElement.Frequency;
                }
                break;
        }
    }

    /**
     * Populate the new Flowsheet with the Care Elements previously edited by the user.
     * Only care components that are not loaded by the backend are overridden.
     * @param oldCareComponents The old care components to compare with
     */
    public applyPendingModificationsToComponents(oldCareComponents: CareComponent[]): void {
        if (!this.CareComponents) {
            this.CareComponents = [];
        }

        const oldCareComponentsMap = toRecord(oldCareComponents);

        this.CareComponents = this.CareComponents.map(currentCareComponent => {
            if (this.isCareComponentImmutable(currentCareComponent)) {
                // Do not override backend-loaded care components
                const oldCareComponent = oldCareComponentsMap[currentCareComponent.Name];
                if (!oldCareComponent)
                    return { ...currentCareComponent };

                return {
                    ...currentCareComponent,
                    Note: oldCareComponent.Note
                };
            }

            const oldCareComponent = oldCareComponentsMap[currentCareComponent.Name];
            if (!oldCareComponent) {
                // No old care component to override with
                return currentCareComponent;
            }

            return {
                ...currentCareComponent,
                CareElements: this.overrideCareElements(
                    currentCareComponent.CareElements,
                    oldCareComponent.CareElements
                ),
                Note: oldCareComponent?.Note
            };
        });
    }

    public isNotApproved(): boolean {
        return this.ApprovalDate == null;
    }
}
